// frontend/src/staff/pages/StaffProgressList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

type MerchantSummary = {
  merchant_id: string;
  company_name: string;
  sales_persons?: string[]; // ✅ 複数営業担当者

  // ✅ From backend /staff/progress
  industry?: string; // 業種

  // ✅ IMPORTANT: backend returns "merchant_type" (法人 / 個人)
  merchant_type?: string; // 区分（法人 / 個人）

  status: string;


  order_status?: "受注" | "失注";



  docs_total: number;
  docs_completed: number;
  uploads_total: number;
  uploads_completed: number;
  updated_at: string;

  // ✅ From backend
  approval_status?: "未承認" | "承認済";

  // ✅ (Optional) If backend later adds it, filter will automatically start working
  sales_person_display?: string; // 営業担当（例: "安成, 吉松"）
};

const BASE_URL = "/api";

function statusStyle(status: string): React.CSSProperties {
  const s = (status || "").trim();

  // ✅ green
  if (s === "承認済") {
    return {
      background: "#ecfdf5",
      border: "1px solid #bbf7d0",
      color: "#166534",
    };
  }

  // ✅ BLUE: submitted waiting approval
  if (s === "提出済（承認待ち）" || s === "提出済") {
    return {
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
    };
  }

  // ✅ red (完了)
  if (s === "完了") {
    return {
      background: "#fff1f2",
      border: "1px solid #fecdd3",
      color: "#b91c1c",
    };
  }

  // ✅ grey (進行中 / default)
  return {
    background: "#f1f5f9",
    border: "1px solid #cbd5e1",
    color: "#334155",
  };
}

function pillBase(): React.CSSProperties {
  return {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  };
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const filterWrap: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
  marginBottom: 12,
};

const filterGrid: React.CSSProperties = {
  display: "grid",
 gridTemplateColumns: "2fr 1fr 1fr 1fr 0.8fr",

  gap: 10,
  alignItems: "end",
};

const filterLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 6,
};

const btnSecondary: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900,
};

export default function StaffProgressList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<MerchantSummary[]>([]);
  const [error, setError] = useState("");

  // ✅ Filters
  const [q, setQ] = useState(""); // 会社名/ID 検索
  const [statusFilter, setStatusFilter] = useState<
    "全て" | "進行中" | "完了" | "提出済（承認待ち）"
    >("全て");

  const [salesFilter, setSalesFilter] = useState<string>("全て");

  const [orderFilter, setOrderFilter] = useState<"全て" | "受注" | "失注">("全て");



  useEffect(() => {
    const run = async () => {
      setError("");
      try {
        const token = localStorage.getItem("staff_google_token") || "";

const res = await fetch(`${BASE_URL}/staff/progress`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "progress fetch failed");

        setRows(json.merchants || []);
        setLoading(false);
      } catch (e: any) {
        setError(e?.message || "進捗情報の取得に失敗しました。");
        setLoading(false);
      }
    };
    run();
  }, []);

  // ✅ Build Sales dropdown candidates from current rows
  const salesCandidates = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      (r.sales_persons || []).forEach((n) => {
        const name = (n || "").trim();
        if (name) set.add(name);
      });
    });
    return ["全て", ...Array.from(set).sort((a, b) => a.localeCompare(b, "ja"))];
  }, [rows]);

  // ✅ Filtering logic
  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();

    return rows.filter((m) => {
      const isApproved = (m.approval_status || "未承認") === "承認済";
      if (isApproved) return false;


      // ✅ submitted判定は「完了(=入力完了)」ではなく、バックエンドが返す提出ステータスで判断する
      const s = (m.status || "").trim();
      const isSubmitted = s.includes("提出済"); // "提出済（承認待ち）" / "提出済"

      // ✅ Status shown on pill
      const displayStatus = isApproved ? "承認済" : isSubmitted ? "提出済（承認待ち）" : s || "進行中";

      // Search by company name or merchant id
      const searchable = `${m.company_name || ""} ${m.merchant_id || ""}`.toLowerCase();
      const okQuery = !qq || searchable.includes(qq);

      // Status filter
      const okStatus = statusFilter === "全て" ? true : displayStatus === statusFilter;

      // Sales filter
      const persons = (m.sales_persons || []).map((x) => (x || "").trim()).filter(Boolean);
      const okSales = salesFilter === "全て" ? true : persons.includes(salesFilter);




      const os = (m.order_status || "受注").trim() as "受注" | "失注";
      const okOrder = orderFilter === "全て" ? true : os === orderFilter;


      return okQuery && okStatus && okSales && okOrder;

    });
  }, [rows, q, statusFilter, salesFilter, orderFilter]);

  const resetFilters = () => {
    setQ("");
    setStatusFilter("全て");
    setSalesFilter("全て");
    setOrderFilter("全て");

  };

  return (
    <StaffLayout title="進捗管理" active="progress">
      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 900, marginBottom: 12 }}>加盟店一覧</div>

          {/* ✅ Filters */}
          <div style={filterWrap}>
            <div style={filterGrid}>
              <div>
                <div style={filterLabel}>検索（会社名 / ID）</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="例：Clinic / M01234567"
                  style={inputStyle}
                />
              </div>

              <div>
                <div style={filterLabel}>ステータス</div>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value as "全て" | "進行中" | "完了" | "提出済（承認待ち）" 
                    )
                  }
                  style={inputStyle}
                >
                  <option value="全て">全て</option>
                  <option value="進行中">進行中</option>
                  <option value="完了">完了</option>
                  <option value="提出済（承認待ち）">提出済（承認待ち）</option>
                  
                </select>
              </div>

              <div>
                <div style={filterLabel}>営業担当者</div>
                <select
                  value={salesFilter}
                  onChange={(e) => setSalesFilter(e.target.value)}
                  style={inputStyle}
                  title={
                    salesCandidates.length <= 1
                      ? "※ 現在、一覧APIに営業担当者が含まれていないため候補が出ません"
                      : "営業担当者で絞り込み"
                  }
                >
                  {salesCandidates.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>









              <div>
  <div style={filterLabel}>受注ステータス</div>
  <select
    value={orderFilter}
    onChange={(e) => setOrderFilter(e.target.value as "全て" | "受注" | "失注")}
    style={inputStyle}
  >
    <option value="全て">全て</option>
    <option value="受注">受注</option>
    <option value="失注">失注</option>
  </select>
</div>

















              <div>
                <div style={filterLabel}>　</div>
                <button type="button" style={btnSecondary} onClick={resetFilters}>
                  クリア
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              表示件数：{filteredRows.length} 件
            </div>
          </div>

          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2.2fr 1fr 1fr 1fr 1fr 1fr 0.9fr",
              gap: 10,
              padding: "10px 12px",
              fontWeight: 900,
              borderBottom: "1px solid #e2e8f0",
              color: "#0f172a",
              fontSize: 13,
            }}
          >
            <div>会社名</div>
            <div>業種</div>
            <div>区分</div>
            <div>ステータス</div>
            <div>フォーム</div>
            <div>書類</div>
            <div>承認</div>
          </div>

          {/* Rows */}
          {filteredRows.map((m) => {
            const isApproved = (m.approval_status || "未承認") === "承認済";

            const s = (m.status || "").trim();
            const isSubmitted = s.includes("提出済"); // ✅ global submit後だけ true

            // ✅ Button enable condition: 提出済 and NOT approved
            const canApprove = isSubmitted && !isApproved;

            // ✅ Status shown on pill
            const displayStatus = isApproved ? "承認済" : isSubmitted ? "提出済（承認待ち）" : s || "進行中";

            // ✅ Button visual state
            const buttonBg = isApproved
              ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)" // green
              : canApprove
              ? "linear-gradient(90deg, #2e5f9e 0%, #1d4ed8 100%)" // ✅ BLUE when waiting approval
              : "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"; // grey

            const buttonBorder = isApproved
              ? "1px solid #16a34a"
              : canApprove
              ? "1px solid #1d4ed8"
              : "1px solid #cbd5e1";

            const buttonTitle = isApproved
              ? "承認済みです"
              : canApprove
              ? "承認する"
              : "提出後に承認できます";

            const buttonLabel = isApproved ? "承認済" : "承認";

            return (
              <div
                key={m.merchant_id}
                onClick={() => navigate(`/staff/progress/${m.merchant_id}`)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2.2fr 1fr 1fr 1fr 1fr 1fr 0.9fr",
                  gap: 10,
                  padding: "12px 12px",
                  borderBottom: "1px solid #f1f5f9",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                title="詳細を見る"
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 900,
                      color: "#0b1f3b",
                      textDecoration: "underline",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.company_name}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    ID: {m.merchant_id}
                  </div>
                </div>

                <div style={{ fontWeight: 800, color: "#334155", fontSize: 13 }}>{m.industry || "-"}</div>

                <div style={{ fontWeight: 800, color: "#334155", fontSize: 13 }}>{m.merchant_type || "-"}</div>

                <div>
                  <span style={{ ...pillBase(), ...statusStyle(displayStatus) }}>{displayStatus}</span>
                </div>

                <div style={{ fontWeight: 900, color: "#334155" }}>
                  {m.docs_completed}/{m.docs_total}
                </div>

                <div style={{ fontWeight: 900, color: "#334155" }}>
                  {m.uploads_completed}/{m.uploads_total}
                </div>

                <div>
                  <button
  disabled={!canApprove}
  onClick={(e) => {
    e.stopPropagation();
    if (!canApprove) return;

    // ✅ Do NOT approve here.
    // ✅ Only navigate to detail page for review.
    navigate(`/staff/progress/${m.merchant_id}`);
  }}
  style={{
    width: "100%",
    padding: "8px 10px",
    borderRadius: 12,
    border: buttonBorder,
    background: buttonBg,
    color: "#fff",
    fontWeight: 900,
    cursor: canApprove ? "pointer" : "not-allowed",
    boxShadow: canApprove ? "0 4px 10px rgba(0,0,0,0.15)" : "none",
    opacity: canApprove || isApproved ? 1 : 0.85,
  }}
  title={buttonTitle}
>
  {buttonLabel}
</button>

                </div>
              </div>
            );
          })}

          {filteredRows.length === 0 && (
            <div style={{ marginTop: 12, color: "#475569", fontWeight: 800 }}>
              条件に一致する加盟店がありません。
            </div>
          )}
        </div>
      )}
    </StaffLayout>
  );
}
